<script setup lang="ts">
import { computed } from 'vue';
import { CARD_LIMITS, CARD_MOTIONS, type CardBackground, type CardLook } from '@vp/shared-types';
import { t } from '../i18n';
import Icon from './Icon.vue';
import Toggle from './Toggle.vue';

/**
 * The look of a profile card or of the tray (module API 4.10): background (none, colour, gradient,
 * picture), its motion, opacity, blur, radius, border and shadow. A floating card in the profile's
 * "Edit" mode, or a section of the card editor (`inline`). "Reset" gives back the default look.
 */
const props = defineProps<{ look: CardLook; title: string; inline?: boolean }>();
const emit = defineEmits<{ change: [look: CardLook]; reset: []; close: [] }>();

const kind = computed(() => props.look.background?.kind ?? 'none');
const gradient = computed(() => (props.look.background?.kind === 'gradient' ? props.look.background : null));
const image = computed(() => (props.look.background?.kind === 'image' ? props.look.background : null));

function put(patch: Partial<CardLook>): void {
  emit('change', { ...props.look, ...patch });
}

function setKind(k: 'none' | CardBackground['kind']): void {
  if (k === 'none') put({ background: null });
  else if (k === 'color') put({ background: { kind: 'color', color: '#5e9cff' } });
  else if (k === 'gradient') put({ background: { kind: 'gradient', type: 'linear', angle: 135, stops: [{ color: '#5e9cff', at: 0 }, { color: '#a855f7', at: 1 }] } });
  else pickImage();
}

function setStop(i: number, color: string): void {
  const g = gradient.value;
  if (g) put({ background: { ...g, stops: g.stops.map((s, j) => (j === i ? { ...s, color } : s)) } });
}

function pickImage(): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/png,image/jpeg,image/webp';
  input.onchange = () => {
    const f = input.files?.[0];
    if (!f || f.size > (CARD_LIMITS.images * 3) / 4) return;
    const r = new FileReader();
    r.onload = () => {
      const src = String(r.result);
      if (/^data:image\/(png|jpeg|webp);base64,/.test(src) && src.length <= CARD_LIMITS.images) put({ background: { kind: 'image', src, fit: 'cover' } });
    };
    r.readAsDataURL(f);
  };
  input.click();
}

const num = (e: Event) => Number((e.target as HTMLInputElement).value);
const val = (e: Event) => (e.target as HTMLInputElement).value;
</script>

<template>
  <section class="look-editor" :class="inline ? 'inline' : 'float card'" role="dialog" :aria-label="t('look.title', { name: title })" data-look-editor @pointerdown.stop @keydown.esc.stop="emit('close')">
    <header class="head">
      <Icon name="palette" :size="16" />
      <strong class="name">{{ inline ? t('look.looks') : t('look.title', { name: title }) }}</strong>
      <button v-if="!inline" type="button" class="icon-btn" :aria-label="t('common.close')" @click="emit('close')"><Icon name="close" :size="15" /></button>
    </header>

    <p class="sec">{{ t('look.background') }}</p>
    <div class="seg-pills wide" role="radiogroup" :aria-label="t('look.background')">
      <button v-for="k in ['none', 'color', 'gradient', 'image'] as const" :key="k" type="button" role="radio" :aria-checked="kind === k" :class="{ active: kind === k }" :data-bg="k" @click="setKind(k)">{{ t('look.bg.' + k) }}</button>
    </div>
    <div v-if="look.background?.kind === 'color'" class="row">
      <span class="lbl">{{ t('look.color') }}</span>
      <label class="swatch-btn" :style="{ background: look.background.color }"><input type="color" :value="look.background.color" :aria-label="t('look.color')" @input="put({ background: { kind: 'color', color: val($event) } })" /></label>
    </div>
    <template v-else-if="gradient">
      <div class="row">
        <span class="lbl">{{ t('look.colors') }}</span>
        <label v-for="(s, i) in gradient.stops" :key="i" class="swatch-btn" :style="{ background: s.color }"><input type="color" :value="s.color" :aria-label="t('look.color')" @input="setStop(i, val($event))" /></label>
      </div>
      <div class="row">
        <span class="lbl">{{ t('look.kind') }}</span>
        <div class="seg-pills" role="radiogroup">
          <button v-for="g in ['linear', 'radial'] as const" :key="g" type="button" role="radio" :aria-checked="gradient.type === g" :class="{ active: gradient.type === g }" @click="put({ background: { ...gradient!, type: g } })">{{ t('look.gradient.' + g) }}</button>
        </div>
      </div>
      <label v-if="gradient.type === 'linear'" class="row">
        <span class="lbl">{{ t('look.angle') }}</span>
        <input class="range" type="range" min="0" max="360" :value="gradient.angle ?? 135" @input="put({ background: { ...gradient!, angle: num($event) } })" />
        <span class="v">{{ gradient.angle ?? 135 }}°</span>
      </label>
    </template>
    <template v-else-if="image">
      <div class="row">
        <span class="lbl">{{ t('look.fit') }}</span>
        <div class="seg-pills" role="radiogroup">
          <button v-for="f in ['cover', 'contain', 'tile'] as const" :key="f" type="button" role="radio" :aria-checked="image.fit === f" :class="{ active: image.fit === f }" @click="put({ background: { ...image!, fit: f } })">{{ t('look.fit.' + f) }}</button>
        </div>
      </div>
      <div class="row">
        <span class="lbl"></span>
        <button type="button" class="pill small" @click="pickImage"><Icon name="image" :size="14" />{{ t('look.otherImage') }}</button>
      </div>
    </template>

    <p class="sec">{{ t('look.motion') }}</p>
    <div class="chips" role="radiogroup" :aria-label="t('look.motion')">
      <button v-for="m in CARD_MOTIONS" :key="m" type="button" role="radio" class="pill small" :aria-checked="(look.motion ?? 'none') === m" :class="{ active: (look.motion ?? 'none') === m }" :data-motion="m" @click="put({ motion: m })">{{ t('look.motions.' + m) }}</button>
    </div>

    <p class="sec">{{ t('look.frame') }}</p>
    <label class="row">
      <span class="lbl">{{ t('look.opacity') }}</span>
      <input class="range" type="range" min="0" max="1" step="0.05" :value="look.opacity ?? 1" data-look="opacity" @input="put({ opacity: num($event) })" />
      <span class="v">{{ Math.round((look.opacity ?? 1) * 100) }}%</span>
    </label>
    <label class="row">
      <span class="lbl">{{ t('look.blur') }}</span>
      <input class="range" type="range" min="0" max="24" :value="look.blur ?? 0" @input="put({ blur: num($event) })" />
      <span class="v">{{ look.blur ?? 0 }}</span>
    </label>
    <label class="row">
      <span class="lbl">{{ t('look.radius') }}</span>
      <input class="range" type="range" min="0" max="32" :value="look.radius ?? 14" data-look="radius" @input="put({ radius: num($event) })" />
      <span class="v">{{ look.radius ?? 14 }}</span>
    </label>
    <div class="row">
      <span class="lbl">{{ t('look.border') }}</span>
      <Toggle :model-value="!!look.border" :label="t('look.border')" @update:model-value="(on) => put({ border: on ? { color: '#5e9cff', width: 2 } : null })" />
      <template v-if="look.border">
        <label class="swatch-btn" :style="{ background: look.border.color }"><input type="color" :value="look.border.color" :aria-label="t('look.color')" @input="put({ border: { ...look.border!, color: val($event) } })" /></label>
        <input class="range short" type="range" min="1" max="4" :value="look.border.width" :aria-label="t('look.borderWidth')" @input="put({ border: { ...look.border!, width: num($event) } })" />
      </template>
    </div>
    <div class="row">
      <span class="lbl">{{ t('look.shadow') }}</span>
      <div class="seg-pills" role="radiogroup" :aria-label="t('look.shadow')">
        <button v-for="s in ['none', 'soft', 'strong'] as const" :key="s" type="button" role="radio" :aria-checked="(look.shadow ?? 'soft') === s" :class="{ active: (look.shadow ?? 'soft') === s }" @click="put({ shadow: s })">{{ t('look.shadows.' + s) }}</button>
      </div>
    </div>

    <footer class="foot">
      <button type="button" class="pill" data-act="look-reset" @click="emit('reset')"><Icon name="refresh" :size="14" />{{ t('look.reset') }}</button>
      <button v-if="!inline" type="button" class="pill primary" data-act="look-done" @click="emit('close')">{{ t('look.done') }}</button>
    </footer>
  </section>
</template>

<style scoped>
.look-editor {
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: default;
}
.look-editor.card {
  position: fixed;
  z-index: 40;
  top: calc(80px + var(--banner-h, 0px));
  right: 16px;
  width: min(380px, calc(100vw - 32px));
  max-height: calc(100vh - 100px - var(--banner-h, 0px));
  overflow: auto;
  padding: 14px 16px;
}
.head {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-2);
}
.name {
  flex: 1;
  min-width: 0;
  color: var(--text);
  font-size: 13.5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sec {
  margin: 8px 0 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-3);
}
.wide {
  align-self: flex-start;
  flex-wrap: wrap;
}
.row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 34px;
}
.lbl {
  flex: 0 0 96px;
  font-size: 13px;
  color: var(--text-2);
}
.range {
  flex: 1;
  min-width: 80px;
  accent-color: var(--accent);
}
.range.short {
  flex: 0 1 90px;
}
.v {
  flex: 0 0 40px;
  text-align: right;
  font-size: 12px;
  color: var(--text-3);
  font-variant-numeric: tabular-nums;
}
.swatch-btn {
  position: relative;
  overflow: hidden;
}
.swatch-btn input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}
.small {
  font-size: 12.5px;
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.foot {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
}
.inline .foot {
  justify-content: flex-start;
}
</style>
